import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from 'services/_infra/rtkBaseQuery';

export const rolesApi = createApi({
  reducerPath: 'rolesApi', // This is the name of the slice in the store
  baseQuery: axiosBaseQuery(), // Custom axios base query (calls apiClient with the query url and params)
  tagTypes: ['Roles'], // Used to track when refetches are needed
  endpoints: (builder) => ({
    getAllRoles: builder.query({
      query: (instance) => ({
        url: '/users/roles',
        method: 'GET',
        msAuth: {
          instance,
          scopes: ['Group.Read.All'],
        },
      }),
      transformResponse: (response) => response.result,
      providesTags: ['Roles'], // Tells RTK Query this query provides 'Roles' data and to refetch when invalidated
    }),
    getRole: builder.query({
      query: (roleId) => ({
        url: `/users/roles/${encodeURIComponent(roleId)}`,
        method: 'GET',
      }),
      transformResponse: (response) => {
        if (Array.isArray(response?.result)) return response.result[0];
        return response?.result ?? response;
      },
      // don't keep role-by-id results in cache between usages
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
      providesTags: ['Roles'],
    }),
    createRole: builder.mutation({
      query: ({ roleName, members, permissions, layout }) => ({
        url: '/users/roles',
        method: 'POST',
        data: {
          roleName,
          members,
          permissions,
          layout,
        },
      }),
      transformResponse: (response) => response.result,
      invalidatesTags: ['Roles'], // Tells RTK Query to refetch any queries that provide this tag
    }),
    updateRole: builder.mutation({
      query: ({ roleId, roleName, members, permissions, layout }) => ({
        url: `/users/roles/${encodeURIComponent(roleId)}`,
        method: 'PUT',
        data: {
          roleName,
          members,
          permissions,
          layout,
        },
      }),
      invalidatesTags: ['Roles'], // Tells RTK Query to refetch any queries that provide this tag
    }),
    deleteRole: builder.mutation({
      query: (deleteId) => ({
        url: `/users/roles/${encodeURIComponent(deleteId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Roles'], // Tells RTK Query to refetch any queries that provide this tag
    }),
    updateRoleHierarchy: builder.mutation({
      query: (hierarchy) => ({
        url: '/users/roles/hierarchy',
        method: 'PATCH',
        data: {
          hierarchy,
        },
      }),
      invalidatesTags: ['Roles'], // Tells RTK Query to refetch any queries that provide this tag
    }),
  }),
});

export const {
  useGetAllRolesQuery,
  useGetRoleQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useUpdateRoleHierarchyMutation,
} = rolesApi;
